import { Subscription } from 'rxjs';

import { ComponentPortal, ComponentType, Portal } from '@angular/cdk/portal';
import { Injectable, InjectionToken, Injector, inject, signal } from '@angular/core';
import { MatSidenav } from '@angular/material/sidenav';

export const SIDEBAR_DATA = new InjectionToken<any>('SIDEBAR_DATA');
export interface SidebarOptions {
  disableClose?: boolean;
  injector?: Injector;
}

@Injectable({
  providedIn: 'root',
})
export class ApmSidebarService {
  /*
   * The value is always assigned as the sidenavRef is set inside the main App Component.
   * */
  #injector = inject(Injector);
  #sidenavRef!: MatSidenav;
  #sidebarContentPortal = signal<Portal<any> | null>(null);

  #sidebarCloseEventSubscription: Subscription | undefined;

  get sidebarContentPortal() {
    return this.#sidebarContentPortal.asReadonly();
  }

  setSidenavRef(sidenav: MatSidenav) {
    this.#sidenavRef = sidenav;
    if (this.#sidebarCloseEventSubscription) {
      this.#sidebarCloseEventSubscription.unsubscribe();
    }
    this.#sidebarCloseEventSubscription = this.#sidenavRef.closedStart.subscribe(() =>
      this.#sidebarContentPortal.set(null)
    );
  }

  /**
   * Opens sidebar with a Component or TemplateRef.
   * @param componentType Component to be rendered inside the sidebar.
   * @param options Optional sidebar data and options.
   */
  openSidebar(componentType: ComponentType<unknown>, data?: any, options?: SidebarOptions) {
    const componentPortal = new ComponentPortal(componentType, null, this.createInjector(data, options?.injector));
    this.#sidenavRef.disableClose = options?.disableClose ?? false;
    if (this.#sidenavRef.opened) {
      this.#sidenavRef.close().then(() => {
        this.#sidebarContentPortal.set(componentPortal);
        this.#sidenavRef.open();
      });
    } else {
      this.#sidebarContentPortal.set(componentPortal);
      this.#sidenavRef.open();
    }
  }

  closeSidebar() {
    this.#sidebarContentPortal.set(null);
    this.#sidenavRef.close();
  }

  toggleSidebar() {
    this.#sidenavRef.toggle();
  }

  private createInjector(data: unknown, injector?: Injector) {
    return Injector.create({
      parent: injector ?? this.#injector,
      providers: [{ provide: SIDEBAR_DATA, useValue: data }],
    });
  }
}
